import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  communityLogin,
  communityLogout,
  fetchMe,
} from "../lib/communityApi";
import { extractApiErrorMessage } from "../lib/format";
import { HTTP_UNAUTHORIZED } from "../constants/http";

// sessionStorage is used (not localStorage) so the session is scoped to the
// browser tab and cleared on close. It is good enough for a community-only
// soft-auth model; it is NOT used for the admin key.
const TOKEN_KEY = "na_sg_community_token";

const readToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (err) {
    console.error("sessionStorage read failed:", err);
    return "";
  }
};
const writeToken = (token) => {
  try {
    if (token) {
      sessionStorage.setItem(TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  } catch (err) {
    console.error("sessionStorage write failed:", err);
  }
};

export function useCommunityAuth() {
  const [token, setToken] = useState(readToken);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(Boolean(readToken()));
  const [signingIn, setSigningIn] = useState(false);

  const signOut = useCallback(async () => {
    const current = token;
    setToken("");
    setMe(null);
    writeToken("");
    if (current) {
      await communityLogout(current);
    }
  }, [token]);

  const loadMe = useCallback(
    async (t) => {
      if (!t) return;
      setLoading(true);
      try {
        const data = await fetchMe(t);
        setMe(data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === HTTP_UNAUTHORIZED) {
          writeToken("");
          setToken("");
          setMe(null);
        } else {
          console.error("fetchMe failed:", err);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) {
      loadMe(token);
    }
  }, [token, loadMe]);

  const signIn = useCallback(async (email) => {
    if (!email || !email.trim()) {
      toast.error("Please enter the email you joined with.");
      return false;
    }
    setSigningIn(true);
    try {
      const res = await communityLogin(email.trim());
      writeToken(res.token);
      setToken(res.token);
      setMe(res.member);
      toast.success(`Welcome back, ${res.member?.first_name || "friend"}.`);
      return true;
    } catch (err) {
      console.error("Community login failed:", err);
      toast.error(
        extractApiErrorMessage(
          err,
          "We couldn't find that email. Please sign up first on the home page."
        )
      );
      return false;
    } finally {
      setSigningIn(false);
    }
  }, []);

  const refreshMe = useCallback(() => loadMe(token), [loadMe, token]);

  return {
    token,
    me,
    setMe,
    isAuthenticated: Boolean(token && me),
    loading,
    signingIn,
    signIn,
    signOut,
    refreshMe,
  };
}
